import React, { useState, useEffect } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'moment/locale/es';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './CalendarView.css';
import { database } from '../firebaseConfig';
import { ref, set, onValue, get } from 'firebase/database';
import { getAuth } from 'firebase/auth';
import emailjs from 'emailjs-com';

// Configurar moment en español con configuración personalizada
moment.locale('es', {
    months: [
        'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
        'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
    ],
    monthsShort: [
        'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun',
        'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'
    ],
    weekdays: [
        'Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'
    ],
    weekdaysShort: ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'],
    weekdaysMin: ['Do', 'Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sá'],
    week: {
        dow: 1, // Lunes como primer día de la semana
        doy: 4  // Primera semana del año
    }
});

const localizer = momentLocalizer(moment);

function CalendarView({ auth }) {

    const getTipoNotaLabel = (tipo) => {
        switch (tipo) {
            case 'efemeride': return 'Efeméride';
            case 'feriado': return 'Feriado';
            case 'solicitud': return 'Solicitud';
            case 'pendiente': return 'Pendiente';
            default: return 'Pendiente';
        }
    };

    const esAutorDeNota = (nota, email) => {
        if (!nota || !email) return false;
        if (typeof nota.autor === 'string') return nota.autor === email;
        return nota.autor?.email === email;
    };

    const getTipoNotaColor = (tipo) => {
        switch (tipo) {
            case 'efemeride': return '#7c3aed';
            case 'feriado': return '#b91c1c';
            case 'solicitud': return '#0f766e';
            case 'pendiente': return '#b45309';
            default: return '#b45309';
        }
    };

    const getTipoDelDia = (notasFecha) => {
        const tipos = (Array.isArray(notasFecha) ? notasFecha : [])
            .map((n) => (n && typeof n === 'object' ? (n.tipo || 'efemeride') : 'efemeride'));

        const tiposUnicos = Array.from(new Set(tipos));
        if (tiposUnicos.length === 1) return tiposUnicos[0];
        if (tiposUnicos.includes('feriado')) return 'feriado';
        if (tiposUnicos.includes('solicitud')) return 'solicitud';
        if (tiposUnicos.includes('pendiente')) return 'pendiente';
        return 'efemeride';
    };

    const normalizarNotas = (data) => {
        if (!data || typeof data !== 'object') return {};
        const normalizadas = {};
        Object.entries(data).forEach(([fecha, notasFecha]) => {
            if (!Array.isArray(notasFecha)) {
                normalizadas[fecha] = [];
                return;
            }
            normalizadas[fecha] = notasFecha.map((nota) => {
                if (!nota || typeof nota !== 'object') return nota;
                if (nota.tipo) return nota;
                return { ...nota, tipo: 'efemeride' };
            });
        });
        return normalizadas;
    };

    // Estado para detectar dispositivos móviles
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    // Estados existentes
    const [selectedDate, setSelectedDate] = useState(null);
    const [showModal, setShowModal] = useState(false);

    // Nuevos estados para las notas
    const [showNotaModal, setShowNotaModal] = useState(false);
    const [notas, setNotas] = useState({});
    const [notaActual, setNotaActual] = useState({ titulo: '', descripcion: '', fecha: null, tipo: 'pendiente' });
    const [notaEditando, setNotaEditando] = useState(null); // { fecha, id }
    const [showSuccessMessage, setShowSuccessMessage] = useState(false);
    const [isGuardandoNota, setIsGuardandoNota] = useState(false);

    // Estados para respuestas
    const [selectedNoteId, setSelectedNoteId] = useState(null); // ID de la nota seleccionada para Masterweb
    const [nuevaRespuesta, setNuevaRespuesta] = useState(''); // Texto de la nueva respuesta
    const [guardandoRespuesta, setGuardandoRespuesta] = useState(false);

    // Detectar cambios en el tamaño de la ventana
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Inicializar EmailJS
    useEffect(() => {
        emailjs.init(process.env.REACT_APP_EMAILJS_PUBLIC_KEY);
    }, []);

    // Manejar botón atrás del móvil para cerrar modales
    useEffect(() => {
        const handlePopState = (event) => {
            if (showModal) {
                setShowModal(false);
                event.preventDefault();
            } else if (showNotaModal) {
                setShowNotaModal(false);
                event.preventDefault();
            }
        };

        // Agregar estado al historial cuando se abre un modal
        if (showModal || showNotaModal) {
            window.history.pushState({ modal: true }, '');
        }

        window.addEventListener('popstate', handlePopState);

        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, [showModal, showNotaModal]);

    // Función para enviar notificación por email de nueva nota
    const enviarNotificacionNota = async (nota, fecha) => {
        const emailServiceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
        if (!emailServiceId) {
            console.warn('REACT_APP_EMAILJS_SERVICE_ID no esta configurado.');
            return;
        }

        const fechaHora = new Date().toLocaleString('es-AR');
        const fechaNota = moment(fecha).format('DD/MM/YYYY');
        const autorNombre = typeof nota.autor === 'object' ?
            (nota.autor.nombre || nota.autor.email) : nota.autor;

        const subject = `Nueva nota en calendario - ${fechaNota}`;
        const message = `Nueva nota agregada al calendario\n\nFecha: ${fechaNota}\nAutor: ${autorNombre}\nTipo: ${getTipoNotaLabel(nota.tipo)}\nTítulo: ${nota.titulo}\nDescripción: ${nota.descripcion || 'Sin descripción'}\nFecha/Hora de creación: ${fechaHora}`;

        try {
            await emailjs.send(emailServiceId, "template_cn46vzz", {
                email: "benitez.gino48@gmail.com",
                subject,
                message
            });
            console.log('Notificación de email enviada exitosamente');
        } catch (error) {
            console.error('Error al enviar notificación por email:', error);
        }
    };

    // Cargar notas desde Firebase
    useEffect(() => {
        const notasRef = ref(database, 'CalendarNotes');
        const unsubscribe = onValue(notasRef, (snapshot) => {
            const data = snapshot.val();
            if (data) {
                setNotas(normalizarNotas(data));
            } else {
                setNotas({});
            }
        }, (error) => {
            console.error('Error al cargar notas del calendario:', error);
        });

        return () => unsubscribe();
    }, []);
    const [calendarEvents, setCalendarEvents] = useState([]);

    // Funciones para manejar notas
    const marcarNotaComoVista = async (fecha, notaId) => {
        console.log('🔍 marcarNotaComoVista llamada:', { fecha, notaId });
        try {
            const auth = getAuth();
            const usuario = auth.currentUser;

            if (!usuario) {
                console.log('❌ No hay usuario autenticado');
                return;
            }

            console.log('👤 Usuario actual:', usuario.email);

            const notasRef = ref(database, `CalendarNotes/${fecha}`);
            const snapshot = await get(notasRef);
            const notasExistentes = snapshot.val() || [];

            console.log('📝 Notas existentes:', notasExistentes);

            if (Array.isArray(notasExistentes)) {
                let notaEncontrada = false;
                const notasActualizadas = notasExistentes.map(nota => {
                    if (nota.id === notaId) {
                        notaEncontrada = true;
                        const vistaPor = nota.vistaPor || [];
                        console.log('👀 VistaPor antes:', vistaPor);

                        if (!vistaPor.includes(usuario.email)) {
                            console.log('✅ Agregando usuario a vistaPor');
                            return {
                                ...nota,
                                vistaPor: [...vistaPor, usuario.email]
                            };
                        } else {
                            console.log('ℹ️ Usuario ya había visto esta nota');
                        }
                    }
                    return nota;
                });

                if (!notaEncontrada) {
                    console.log('❌ Nota no encontrada con ID:', notaId);
                    return;
                }

                console.log('💾 Actualizando base de datos...');
                // Actualizar la base de datos
                await set(notasRef, notasActualizadas);

                console.log('🔄 Actualizando estado local...');
                // Actualizar el estado local inmediatamente
                setNotas(prevNotas => ({
                    ...prevNotas,
                    [fecha]: notasActualizadas
                }));

                // Forzar re-renderización del calendario
                setCalendarEvents(prevEvents => [...prevEvents]);

                console.log('✅ Nota marcada como vista exitosamente');
            }
        } catch (error) {
            console.error('❌ Error al marcar nota como vista:', error);
        }
    };

    const abrirModalNota = (fecha = null) => {
        setNotaActual({
            titulo: '',
            descripcion: '',
            fecha: fecha || selectedDate,
            tipo: 'pendiente'
        });
        setNotaEditando(null);
        setShowNotaModal(true);
    };

    const cerrarModalNota = () => {
        setShowNotaModal(false);
        setNotaActual({ titulo: '', descripcion: '', fecha: null, tipo: 'pendiente' });
        setNotaEditando(null);
        setShowSuccessMessage(false);
        setIsGuardandoNota(false);
    };

    const guardarNota = async () => {
        if (!notaActual.titulo.trim() || !notaActual.fecha) return;

        setIsGuardandoNota(true);
        setShowSuccessMessage(false);

        try {
            const auth = getAuth();
            const usuario = auth.currentUser;

            if (!usuario) {
                console.error('Usuario no autenticado');
                alert('Debes estar autenticado para crear notas.');
                setIsGuardandoNota(false);
                return;
            }

            // Guardar en Firebase
            const fechaKey = notaActual.fecha;
            const notasRef = ref(database, `CalendarNotes/${fechaKey}`);

            // Obtener notas existentes para esa fecha
            const snapshot = await get(notasRef);
            const notasExistentes = snapshot.val() || [];

            let notasActualizadas = Array.isArray(notasExistentes) ? [...notasExistentes] : [];

            if (notaEditando && notaEditando.fecha === fechaKey && notaEditando.id) {
                const notaOriginal = notasActualizadas.find((n) => n?.id === notaEditando.id);
                if (!esAutorDeNota(notaOriginal, usuario.email)) {
                    alert('Solo el creador puede editar esta nota.');
                    setIsGuardandoNota(false);
                    return;
                }

                notasActualizadas = notasActualizadas.map((n) => {
                    if (n?.id !== notaEditando.id) return n;
                    return {
                        ...n,
                        titulo: notaActual.titulo.trim(),
                        descripcion: notaActual.descripcion.trim(),
                        tipo: notaActual.tipo || 'pendiente'
                    };
                });
            } else {
                const nuevaNota = {
                    id: Date.now().toString(),
                    titulo: notaActual.titulo.trim(),
                    descripcion: notaActual.descripcion.trim(),
                    fecha: notaActual.fecha,
                    tipo: notaActual.tipo || 'pendiente',
                    autor: {
                        email: usuario.email,
                        nombre: usuario.displayName || usuario.email
                    },
                    fechaCreacion: new Date().toISOString(),
                    vistaPor: [usuario.email]
                };
                notasActualizadas = [...notasActualizadas, nuevaNota];
                await enviarNotificacionNota(nuevaNota, fechaKey);
            }

            await set(notasRef, notasActualizadas);

            console.log('Nota guardada exitosamente');

            // Mostrar mensaje de confirmación
            setShowSuccessMessage(true);
            setIsGuardandoNota(false);

            // Limpiar formulario
            setNotaActual({ titulo: '', descripcion: '', fecha: notaActual.fecha, tipo: notaActual.tipo || 'pendiente' });
            setNotaEditando(null);

            // Cerrar modal después de 2 segundos
            setTimeout(() => {
                cerrarModalNota();
            }, 2000);

        } catch (error) {
            console.error('Error al guardar nota:', error);
            alert('Error al guardar la nota. Por favor, intenta de nuevo.');
            setIsGuardandoNota(false);
        }
    };

    const editarNota = (fecha, nota) => {
        if (!fecha || !nota) return;
        const auth = getAuth();
        const usuario = auth.currentUser;
        if (!usuario) {
            alert('Debes estar autenticado para editar.');
            return;
        }
        if (!esAutorDeNota(nota, usuario.email)) {
            alert('Solo el creador puede editar esta nota.');
            return;
        }

        setNotaActual({
            titulo: nota.titulo || '',
            descripcion: nota.descripcion || '',
            fecha,
            tipo: nota.tipo || 'pendiente'
        });
        setNotaEditando({ fecha, id: nota.id });
        setShowNotaModal(true);
    };

    const borrarNota = async (fecha, notaId) => {
        if (!fecha || !notaId) return;
        const confirmar = window.confirm('¿Borrar esta nota? Esta acción no se puede deshacer.');
        if (!confirmar) return;

        try {
            const auth = getAuth();
            const usuario = auth.currentUser;
            if (!usuario) {
                alert('Debes estar autenticado para borrar.');
                return;
            }

            const notasRef = ref(database, `CalendarNotes/${fecha}`);
            const snapshot = await get(notasRef);
            const notasExistentes = snapshot.val() || [];
            if (!Array.isArray(notasExistentes)) return;

            const notaObjetivo = notasExistentes.find((n) => n?.id === notaId);
            if (!esAutorDeNota(notaObjetivo, usuario.email)) {
                alert('Solo el creador puede borrar esta nota.');
                return;
            }

            const notasActualizadas = notasExistentes.filter((n) => n?.id !== notaId);
            await set(notasRef, notasActualizadas);

            setNotas((prev) => ({
                ...prev,
                [fecha]: notasActualizadas
            }));

            if (selectedDate === fecha && selectedNoteId === notaId) {
                const siguiente = notasActualizadas[0]?.id || null;
                setSelectedNoteId(siguiente);
            }
        } catch (error) {
            console.error('Error al borrar nota:', error);
            alert('Error al borrar la nota. Por favor, intenta de nuevo.');
        }
    };

    // Función para agregar respuesta a una nota
    const guardarRespuesta = async (fecha, notaId) => {
        if (!nuevaRespuesta.trim()) {
            alert('Por favor, escribe una respuesta.');
            return;
        }

        setGuardandoRespuesta(true);

        try {
            const auth = getAuth();
            const usuario = auth.currentUser;

            if (!usuario) {
                console.error('Usuario no autenticado');
                alert('Debes estar autenticado para responder.');
                setGuardandoRespuesta(false);
                return;
            }

            const notasRef = ref(database, `CalendarNotes/${fecha}`);
            const snapshot = await get(notasRef);
            const notasExistentes = snapshot.val() || [];

            if (Array.isArray(notasExistentes)) {
                const notasActualizadas = notasExistentes.map(nota => {
                    if (nota.id === notaId) {
                        const respuestas = nota.respuestas || [];
                        const nuevaResp = {
                            id: Date.now().toString(),
                            texto: nuevaRespuesta.trim(),
                            autor: {
                                email: usuario.email,
                                nombre: usuario.displayName || usuario.email
                            },
                            fecha: new Date().toISOString()
                        };

                        return {
                            ...nota,
                            respuestas: [...respuestas, nuevaResp]
                        };
                    }
                    return nota;
                });

                await set(notasRef, notasActualizadas);

                console.log('Respuesta guardada exitosamente');
                setNuevaRespuesta('');
                setGuardandoRespuesta(false);
            }
        } catch (error) {
            console.error('Error al guardar respuesta:', error);
            alert('Error al guardar la respuesta. Por favor, intenta de nuevo.');
            setGuardandoRespuesta(false);
        }
    };


    // Convertir notas a eventos del calendario
    useEffect(() => {
        const events = [];

        // Crear eventos solo para fechas que tienen notas
        Object.entries(notas).forEach(([fecha, notasFecha]) => {
            if (notasFecha.length > 0) {
                const date = moment(fecha).toDate();
                const today = moment().startOf('day');
                const eventDate = moment(fecha).startOf('day');

                // Determinar si la fecha es pasada, actual o futura
                const isPast = eventDate.isBefore(today);
                const isToday = eventDate.isSame(today);
                const isFuture = eventDate.isAfter(today);

                // Verificar si hay notas nuevas para el usuario actual
                const currentUser = getAuth().currentUser;
                const hasNewNotes = currentUser && notasFecha.some(nota =>
                    !nota.vistaPor || !nota.vistaPor.includes(currentUser.email)
                );

                const cantidadNotas = notasFecha.length;
                const cantidadNotasNuevas = currentUser ? notasFecha.filter(nota =>
                    !nota.vistaPor || !nota.vistaPor.includes(currentUser.email)
                ).length : 0;

                let title = `${cantidadNotas} nota${cantidadNotas > 1 ? 's' : ''}`;
                if (hasNewNotes && cantidadNotasNuevas > 0) {
                    title += ` (${cantidadNotasNuevas} nueva${cantidadNotasNuevas > 1 ? 's' : ''})`;
                }

                const tipoDelDia = getTipoDelDia(notasFecha);
                const tiposUnicos = Array.from(new Set(notasFecha.map((n) => (n && typeof n === 'object' ? (n.tipo || 'efemeride') : 'efemeride'))));
                title += ` - ${tiposUnicos.length === 1 ? getTipoNotaLabel(tipoDelDia) : 'Mixto'}`;

                let backgroundColor = getTipoNotaColor(tipoDelDia);
                let borderColor = hasNewNotes ? '#ef4444' : backgroundColor;

                // Aplicar modificaciones visuales según la fecha
                let opacity = 1;
                let borderWidth = '2px';
                let borderStyle = 'solid';
                let fontWeight = 'normal';
                let boxShadow = `0 0 0 2px ${backgroundColor}`;

                if (isPast) {
                    opacity = 0.6;
                    borderWidth = '1px';
                    fontWeight = '300';
                } else if (isToday) {
                    borderWidth = '3px';
                    fontWeight = 'bold';
                    boxShadow = `0 0 10px rgba(0, 0, 0, 0.15), 0 0 0 2px ${backgroundColor}`;
                } else if (isFuture) {
                    borderWidth = '2px';
                    fontWeight = '600';
                    boxShadow = `0 2px 4px rgba(0, 0, 0, 0.1), 0 0 0 2px ${backgroundColor}`;
                }

                if (hasNewNotes) {
                    boxShadow = `${boxShadow}, 0 0 0 3px rgba(239, 68, 68, 0.28)`;
                }

                events.push({
                    id: `notas-${fecha}`,
                    title,
                    start: date,
                    end: date,
                    allDay: true,
                    resource: {
                        fecha,
                        isPast,
                        isToday,
                        isFuture,
                        hasNewNotes,
                        tipoDelDia
                    },
                    style: {
                        backgroundColor,
                        borderColor,
                        color: 'white',
                        opacity,
                        borderWidth,
                        borderStyle,
                        fontWeight,
                        boxShadow
                    }
                });
            }
        });

        setCalendarEvents(events);
    }, [notas]);


    // Manejar clic en evento del calendario
    const handleSelectEvent = (event) => {
        const dateStr = moment(event.start).format('YYYY-MM-DD');
        const tieneNotas = notas[dateStr] && notas[dateStr].length > 0;

        if (tieneNotas) {
            setSelectedDate(dateStr);
            if (notas[dateStr] && notas[dateStr].length > 0) {
                setSelectedNoteId(notas[dateStr][0].id);
            } else {
                setSelectedNoteId(null);
            }
            setShowModal(true);
        } else {
            setNotaActual({
                titulo: '',
                descripcion: '',
                fecha: dateStr,
                tipo: 'pendiente'
            });
            setShowNotaModal(true);
        }
    };

    // Manejar clic en slot vacío del calendario
    const handleSelectSlot = (slotInfo) => {
        const dateStr = moment(slotInfo.start).format('YYYY-MM-DD');
        const tieneNotas = notas[dateStr] && notas[dateStr].length > 0;

        if (tieneNotas) {
            setSelectedDate(dateStr);
            if (notas[dateStr] && notas[dateStr].length > 0) {
                setSelectedNoteId(notas[dateStr][0].id);
            } else {
                setSelectedNoteId(null);
            }
            setShowModal(true);
        } else {
            setNotaActual({
                titulo: '',
                descripcion: '',
                fecha: dateStr,
                tipo: 'pendiente'
            });
            setShowNotaModal(true);
        }
    };


    // Personalizar estilos de eventos
    const eventStyleGetter = (event) => {
        let className = '';

        // Agregar clases CSS basadas en el tipo de fecha
        if (event.resource.isToday) {
            className = 'today-event';
        } else if (event.resource.isFuture) {
            className = 'future-event';
        } else if (event.resource.isPast) {
            className = 'past-event';
        }

        // Agregar clase especial para publicaciones nuevas
        // if (event.resource.hasNewPublications) { // This is related to publications, which are being removed.
        //     className += ' new-publication-event';
        // }

        // Agregar clase especial para notas nuevas
        if (event.resource.hasNewNotes) {
            className += ' new-notes-event';
        }

        return {
            style: event.style,
            className: className.trim()
        };
    };



    // Obtener color del estado
    // const getEstadoColor = (estado, tipo) => { // This function is related to publications, which are being removed.
    //     if (tipo === 'historia_diaria') return '#007bff';

    //     switch (estado) {
    //         case 'confirmado': return '#28a745';
    //         case 'pendiente':
    //         case 'nuevo': return '#ffc107';
    //         case 'rechazado': return '#dc3545';
    //         default: return '#6c757d';
    //     }
    // };

    // Obtener texto del estado
    // const getEstadoTexto = (estado, tipo) => { // This function is related to publications, which are being removed.
    //     if (tipo === 'historia_diaria') return 'Historia Diaria';

    //     switch (estado) {
    //         case 'confirmado': return 'Confirmada';
    //         case 'pendiente': return 'Pendiente';
    //         case 'nuevo': return 'Nueva';
    //         case 'rechazado': return 'Rechazada';
    //         default: return 'Sin estado';
    //     }
    // };

    return (
        <div className="calendar-view-container">
            <div className="calendar-header">
                <h2>Calendario de Notas</h2>
                <div className="calendar-legend">
                    <div className="legend-item">
                        <span className="legend-color" style={{ backgroundColor: '#e74c3c' }}></span>
                        <span>Notas Nuevas</span>
                    </div>
                    <div className="legend-item">
                        <span className="legend-color" style={{ backgroundColor: '#ff6b35' }}></span>
                        <span>Notas Vistas</span>
                    </div>
                </div>
            </div>

            <div className="calendar-wrapper">
                <Calendar
                    localizer={localizer}
                    events={calendarEvents}
                    startAccessor="start"
                    endAccessor="end"
                    style={{ height: isMobile ? 'calc(100vh - 180px)' : 600 }}
                    onSelectEvent={handleSelectEvent}
                    onSelectSlot={handleSelectSlot}
                    selectable
                    eventPropGetter={eventStyleGetter}
                    views={isMobile ? ['month', 'agenda'] : ['month']}
                    defaultView="month"
                    onNavigate={() => { }} // Prevenir navegación automática
                    onDrillDown={(date) => {
                        // En lugar de cambiar de vista, abrir el modal
                        const dateStr = moment(date).format('YYYY-MM-DD');
                        const tieneNotas = notas[dateStr] && notas[dateStr].length > 0;

                        if (tieneNotas) {
                            setSelectedDate(dateStr);
                            if (notas[dateStr] && notas[dateStr].length > 0) {
                                setSelectedNoteId(notas[dateStr][0].id);
                            } else {
                                setSelectedNoteId(null);
                            }
                            setShowModal(true);
                        } else {
                            setNotaActual({
                                titulo: '',
                                descripcion: '',
                                fecha: dateStr,
                                tipo: 'pendiente'
                            });
                            setNotaEditando(null);
                            setShowNotaModal(true);
                        }
                    }}
                    messages={{
                        next: "Siguiente",
                        previous: "Anterior",
                        today: "Hoy",
                        month: "Mes",
                        week: "Semana",
                        day: "Día",
                        agenda: "Agenda",
                        date: "Fecha",
                        time: "Hora",
                        event: "Evento",
                        allDay: "Todo el día",
                        work_week: "Semana laboral",
                        yesterday: "Ayer",
                        tomorrow: "Mañana",
                        noEventsInRange: "No hay publicaciones en este rango de fechas.",
                        showMore: total => `+ Ver ${total} más`
                    }}
                />
            </div>

            {/* Modal para mostrar notas del día */}
            {showModal && (
                <div className="calendar-modal-overlay" onClick={() => setShowModal(false)}>
                    <div className="calendar-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="calendar-modal-header">
                            <h3>
                                Notas del {moment(selectedDate).format('DD [de] MMMM [de] YYYY')}
                            </h3>
                            <div className="calendar-modal-actions">
                                <button
                                    className="add-note-btn"
                                    onClick={() => abrirModalNota(selectedDate)}
                                    title="Agregar nota a esta fecha"
                                >
                                    + Nota
                                </button>
                                <button
                                    className="calendar-modal-close"
                                    onClick={() => setShowModal(false)}
                                >
                                    ×
                                </button>
                            </div>
                        </div>

                        <div className="calendar-modal-body">
                            {/* Mostrar notas existentes para esta fecha */}
                            {notas[selectedDate] && notas[selectedDate].length > 0 && (
                                <div className="notes-layout-container">
                                    {/* Sidebar: Lista de notas */}
                                    <div className="notes-sidebar">
                                        <h4>📝 Notas del día</h4>
                                        <div className="notes-sidebar-list">
                                            {notas[selectedDate].map((nota) => {
                                                const auth = getAuth();
                                                const usuario = auth.currentUser;
                                                const esNueva = usuario && nota.vistaPor && !nota.vistaPor.includes(usuario.email);
                                                const isSelected = selectedNoteId === nota.id;
                                                const tipo = nota.tipo || 'pendiente';

                                                return (
                                                    <div
                                                        key={nota.id}
                                                        className={`note-sidebar-item ${isSelected ? 'selected' : ''} ${esNueva ? 'note-nueva' : ''}`}
                                                        onClick={() => {
                                                            setSelectedNoteId(nota.id);
                                                            if (esNueva) {
                                                                marcarNotaComoVista(selectedDate, nota.id);
                                                            }
                                                        }}
                                                    >
                                                        <div className="note-sidebar-title">
                                                            {nota.titulo.length > 12 ? nota.titulo.substring(0, 12) + '...' : nota.titulo}
                                                        </div>
                                                        <div className="note-sidebar-meta">
                                                            <span className={`note-type-badge note-type-${tipo}`}>
                                                                {getTipoNotaLabel(tipo)}
                                                            </span>
                                                            {esNueva && <span className="note-badge-nuevo-mini">NUEVO</span>}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    {/* Content: Detalle de la nota seleccionada */}
                                    <div className="notes-content-area">
                                        {(() => {
                                            const selectedNote = notas[selectedDate].find(n => n.id === selectedNoteId);

                                            if (!selectedNote) return (
                                                <div className="notes-empty-selection">
                                                    Selecciona una nota para ver los detalles
                                                </div>
                                            );

                                            const cantidadRespuestas = selectedNote.respuestas ? selectedNote.respuestas.length : 0;
                                            const auth = getAuth();
                                            const usuario = auth.currentUser;
                                            const tipo = selectedNote.tipo || 'pendiente';
                                            const puedeEditar = usuario && esAutorDeNota(selectedNote, usuario.email);

                                            return (
                                                <div className="note-detail-view">
                                                    <div className="note-detail-header">
                                                        <div className="note-detail-title-row">
                                                            <h2>{selectedNote.titulo}</h2>
                                                            <span className="note-detail-date">
                                                                {moment(selectedNote.fechaCreacion).format('DD/MM/YYYY HH:mm')}
                                                            </span>
                                                        </div>
                                                        <div className="note-detail-meta">
                                                            <span className="note-detail-author">
                                                                Por: {selectedNote.autor?.nombre || selectedNote.autor?.email || selectedNote.autor}
                                                            </span>
                                                            <div className="note-detail-actions">
                                                                <span className={`note-type-badge note-type-${tipo}`}>
                                                                    {getTipoNotaLabel(tipo)}
                                                                </span>
                                                                {puedeEditar && (
                                                                    <>
                                                                        <button
                                                                            type="button"
                                                                            className="note-action-btn note-action-edit"
                                                                            onClick={() => editarNota(selectedDate, selectedNote)}
                                                                        >
                                                                            Editar
                                                                        </button>
                                                                        <button
                                                                            type="button"
                                                                            className="note-action-btn note-action-delete"
                                                                            onClick={() => borrarNota(selectedDate, selectedNote.id)}
                                                                        >
                                                                            Borrar
                                                                        </button>
                                                                    </>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {selectedNote.descripcion && (
                                                        <div className="note-detail-description">
                                                            {selectedNote.descripcion}
                                                        </div>
                                                    )}

                                                    <div className="note-detail-replies-section">
                                                        <div className="replies-header">
                                                            Respuestas ({cantidadRespuestas})
                                                        </div>

                                                        {selectedNote.respuestas && selectedNote.respuestas.length > 0 ? (
                                                            <div className="replies-list">
                                                                {selectedNote.respuestas.map((respuesta) => (
                                                                    <div key={respuesta.id} className="reply-item">
                                                                        <div className="reply-header">
                                                                            <strong>{respuesta.autor?.nombre || respuesta.autor?.email}</strong>
                                                                            <span className="reply-date">
                                                                                {moment(respuesta.fecha).format('DD/MM/YYYY HH:mm')}
                                                                            </span>
                                                                        </div>
                                                                        <div className="reply-text">
                                                                            {respuesta.texto}
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        ) : (
                                                            <div className="no-replies-message">
                                                                No hay respuestas aún. Sé el primero en responder.
                                                            </div>
                                                        )}

                                                        <div className="reply-form">
                                                            <div className="reply-form-header">Agregar respuesta</div>
                                                            <textarea
                                                                value={selectedNoteId === selectedNote.id ? nuevaRespuesta : ''}
                                                                onChange={(e) => {
                                                                    // Asegurarse de que estamos respondiendo a la nota correcta
                                                                    if (selectedNoteId !== selectedNote.id) setSelectedNoteId(selectedNote.id);
                                                                    setNuevaRespuesta(e.target.value);
                                                                }}
                                                                placeholder="Escribe tu respuesta..."
                                                                rows={3}
                                                                disabled={guardandoRespuesta}
                                                            />
                                                            <button
                                                                className="btn-send-reply"
                                                                onClick={() => guardarRespuesta(selectedDate, selectedNote.id)}
                                                                disabled={!nuevaRespuesta.trim() || guardandoRespuesta}
                                                            >
                                                                {guardandoRespuesta ? 'Enviando...' : 'Enviar Respuesta'}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })()}
                                    </div>
                                </div>
                            )}

                            {/* Mensaje si no hay notas */}
                            {(!notas[selectedDate] || notas[selectedDate].length === 0) && (
                                <div className="no-publications">
                                    No hay notas para este día. Haz clic en "+ Nota" para agregar una.
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Modal para agregar/editar notas */}
            {showNotaModal && (
                <div className="calendar-modal-overlay" onClick={cerrarModalNota}>
                    <div className="calendar-modal note-modal-large" onClick={(e) => e.stopPropagation()}>
                        <div className="calendar-modal-header note-modal-header-enhanced">
                            <div className="note-modal-header-content">
                                <div>
                                    <h3>{notaEditando ? 'Editar Nota' : 'Crear Nueva Nota'}</h3>
                                    <p className="note-modal-date">{moment(notaActual.fecha).format('dddd, DD [de] MMMM [de] YYYY')}</p>
                                </div>
                            </div>
                            <button
                                className="calendar-modal-close"
                                onClick={cerrarModalNota}
                                title="Cerrar"
                            >
                                ×
                            </button>
                        </div>

                        <div className="calendar-modal-body note-modal-body-enhanced">
                            {showSuccessMessage && (
                                <div className="success-message-enhanced">
                                    <div className="success-icon">✓</div>
                                    <div>
                                        <strong>¡Nota guardada exitosamente!</strong>
                                        <p>El modal se cerrará automáticamente</p>
                                    </div>
                                </div>
                            )}

                            <div className="note-form-enhanced">
                                <div className="form-group-enhanced">
                                    <label htmlFor="nota-titulo" className="form-label-enhanced">
                                        <span>Título de la nota</span>
                                        <span className="label-required">*</span>
                                    </label>
                                    <input
                                        id="nota-titulo"
                                        type="text"
                                        className="form-input-enhanced"
                                        value={notaActual.titulo}
                                        onChange={(e) => setNotaActual({
                                            ...notaActual,
                                            titulo: e.target.value
                                        })}
                                        placeholder="Ej: Recordatorio importante..."
                                        maxLength={100}
                                        disabled={isGuardandoNota || showSuccessMessage}
                                    />
                                    <div className="char-counter">{notaActual.titulo.length}/100</div>
                                </div>

                                <div className="form-group-enhanced">
                                    <label htmlFor="nota-tipo" className="form-label-enhanced">
                                        <span>Tipo</span>
                                        <span className="label-required">*</span>
                                    </label>
                                    <div id="nota-tipo" className="note-type-options" aria-label="Tipo de nota">
                                        <button
                                            type="button"
                                            className={`note-type-option note-type-option-efemeride ${((notaActual.tipo || 'pendiente') === 'efemeride') ? 'selected' : ''}`}
                                            onClick={() => setNotaActual({ ...notaActual, tipo: 'efemeride' })}
                                            disabled={isGuardandoNota || showSuccessMessage}
                                        >
                                            Efeméride
                                        </button>
                                        <button
                                            type="button"
                                            className={`note-type-option note-type-option-feriado ${((notaActual.tipo || 'pendiente') === 'feriado') ? 'selected' : ''}`}
                                            onClick={() => setNotaActual({ ...notaActual, tipo: 'feriado' })}
                                            disabled={isGuardandoNota || showSuccessMessage}
                                        >
                                            Feriado
                                        </button>
                                        <button
                                            type="button"
                                            className={`note-type-option note-type-option-solicitud ${((notaActual.tipo || 'pendiente') === 'solicitud') ? 'selected' : ''}`}
                                            onClick={() => setNotaActual({ ...notaActual, tipo: 'solicitud' })}
                                            disabled={isGuardandoNota || showSuccessMessage}
                                        >
                                            Solicitud
                                        </button>
                                        <button
                                            type="button"
                                            className={`note-type-option note-type-option-pendiente ${((notaActual.tipo || 'pendiente') === 'pendiente') ? 'selected' : ''}`}
                                            onClick={() => setNotaActual({ ...notaActual, tipo: 'pendiente' })}
                                            disabled={isGuardandoNota || showSuccessMessage}
                                        >
                                            Pendiente
                                        </button>
                                    </div>
                                </div>

                                <div className="form-group-enhanced">
                                    <label htmlFor="nota-descripcion" className="form-label-enhanced">
                                        <span>Descripción</span>
                                        <span className="label-optional">(opcional)</span>
                                    </label>
                                    <textarea
                                        id="nota-descripcion"
                                        className="form-textarea-enhanced"
                                        value={notaActual.descripcion}
                                        onChange={(e) => setNotaActual({
                                            ...notaActual,
                                            descripcion: e.target.value
                                        })}
                                        placeholder="Agrega detalles adicionales sobre esta nota..."
                                        rows={6}
                                        maxLength={500}
                                        disabled={isGuardandoNota || showSuccessMessage}
                                    />
                                    <div className="char-counter">{notaActual.descripcion.length}/500</div>
                                </div>

                                <div className="form-actions-enhanced">
                                    <button
                                        className="btn-cancel-enhanced"
                                        onClick={cerrarModalNota}
                                        disabled={isGuardandoNota}
                                    >
                                        {showSuccessMessage ? 'Cerrar' : 'Cancelar'}
                                    </button>
                                    <button
                                        className="btn-save-enhanced"
                                        onClick={guardarNota}
                                        disabled={!notaActual.titulo.trim() || isGuardandoNota || showSuccessMessage}
                                    >
                                        {isGuardandoNota ? (
                                            <>
                                                <span className="spinner"></span>
                                                Guardando...
                                            </>
                                        ) : (
                                            (notaEditando ? 'Guardar Cambios' : 'Guardar Nota')
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default CalendarView;
